// src/features/organisation/components/employees/EmployeeManagement.tsx
import React, { useState, useMemo } from 'react';
import { useEmployees, useEmployeeActions } from '../../hooks/useEmployees';
import { Employee } from '../../../../types/organisation';
import { TableColumn } from '../../../../types';
import {
  Users,
  Plus,
  Trash2,
  Mail,
  Shield,
  Search,
  X,
  Globe
} from 'lucide-react';
import DataTable from '../../../../components/ui/DataTable';
import EmployeeDetailForm from './EmployeeDetailForm';
import OrganisationNavigation from '../OrganisationNavigation';
import EmployeeQuickForm from './EmployeeQuickForm';

const EmployeeManagement: React.FC = () => {
  const { employees, loading, error, refresh } = useEmployees();
  const { deleteEmployee } = useEmployeeActions();

  const [showQuickForm, setShowQuickForm] = useState(false);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<number | null>(null);
  const [selectedRows, setSelectedRows] = useState<Employee[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [qhseFilter, setQhseFilter] = useState('all');

  const filteredEmployees = useMemo(() => {
    return (employees || []).filter(emp => {
      const matchesSearch = !searchTerm ||
        emp.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        emp.employeeCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
        emp.email.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesStatus = statusFilter === 'all' ||
        (statusFilter === 'active' && emp.employeeStatus.code === 'ACTIVE') ||
        (statusFilter === 'inactive' && emp.employeeStatus.code !== 'ACTIVE');

      const matchesQhse = qhseFilter === 'all' ||
        (qhseFilter === 'qhse' && emp.isQhseResponsible) ||
        (qhseFilter === 'non-qhse' && !emp.isQhseResponsible);

      return matchesSearch && matchesStatus && matchesQhse;
    });
  }, [employees, searchTerm, statusFilter, qhseFilter]);

  const columns: TableColumn<Employee>[] = [
    {
      key: 'employeeCode',
      title: 'Code',
      sortable: true,
    },
    {
      key: 'fullName',
      title: 'Nom complet',
      sortable: true,
      render: (value: string, record: Employee) => (
        <div className="flex items-center space-x-3">
          <div className="flex-shrink-0">
            <div className="h-8 w-8 rounded-full bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center">
              <span className="text-sm font-medium text-white">
                {record.firstName[0]}{record.lastName[0]}
              </span>
            </div>
          </div>
          <div>
            <div className="font-medium text-gray-900">{value}</div>
            <div className="flex items-center space-x-2">
              <Mail className="h-3 w-3 text-gray-400" />
              <span className="text-sm text-gray-500">{record.email}</span>
            </div>
          </div>
        </div>
      ),
    },
    {
      key: 'position',
      title: 'Poste',
      render: (value: unknown) => value?.positionName || 'Non défini',
    },
    {
      key: 'department',
      title: 'Département',
      render: (value: unknown) => value?.departmentName || 'Non défini',
    },
    {
      key: 'site',
      title: 'Site',
      render: (value: unknown) => value?.siteName || 'N/A',
    },
    {
      key: 'isQhseResponsible',
      title: 'QHSE',
      render: (value: boolean) => value ? (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          <Shield className="h-3 w-3 mr-1" />
          QHSE
        </span>
      ) : null,
    },
    {
      key: 'roleAssignments',
      title: 'Rôles',
      render: (value: unknown[]) => (
        <span className="text-sm text-gray-600">
          {value?.length || 0} rôle{(value?.length || 0) > 1 ? 's' : ''}
        </span>
      ),
    },
  ];

  const handleBulkDelete = async () => {
    if (selectedRows.length === 0) return;
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer ${selectedRows.length} employé(s) ?`)) return;
    try {
      await Promise.all(selectedRows.map(emp => deleteEmployee(emp.id)));
      setSelectedRows([]);
      await refresh();
    } catch {
      alert('Erreur lors de la suppression des employés');
    }
  };

  const handleDetailFormClose = async () => {
    setSelectedEmployeeId(null);
    await refresh();
  };

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4">
        <div className="text-red-600">
          Erreur lors du chargement des employés: {error.message}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <OrganisationNavigation />

      {/* En-tête */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center space-y-4 md:space-y-0">
        <div className="flex items-center space-x-3">
          <Users className="h-8 w-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Gestion des Employés</h1>
            <p className="text-gray-600">Gérez vos employés et leurs informations</p>
          </div>
        </div>
        <button
          onClick={() => setShowQuickForm(true)}
          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          <Plus className="h-4 w-4" />
          <span>Nouvel employé</span>
        </button>
      </div>

      {/* Statistiques */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
        <div className="bg-white rounded-lg shadow border border-gray-200 p-4 md:p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0 bg-blue-500 rounded-md p-2 md:p-3">
              <Users className="h-5 w-5 md:h-6 md:w-6 text-white" />
            </div>
            <div className="ml-3 md:ml-4">
              <div className="text-xl md:text-2xl font-bold text-gray-900">{(employees || []).length}</div>
              <div className="text-xs md:text-sm text-gray-600">Total employés</div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow border border-gray-200 p-4 md:p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0 bg-green-500 rounded-md p-2 md:p-3">
              <Shield className="h-5 w-5 md:h-6 md:w-6 text-white" />
            </div>
            <div className="ml-3 md:ml-4">
              <div className="text-xl md:text-2xl font-bold text-gray-900">
                {(employees || []).filter(e => e.isQhseResponsible).length}
              </div>
              <div className="text-xs md:text-sm text-gray-600">Responsables QHSE</div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow border border-gray-200 p-4 md:p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0 bg-purple-500 rounded-md p-2 md:p-3">
              <Users className="h-5 w-5 md:h-6 md:w-6 text-white" />
            </div>
            <div className="ml-3 md:ml-4">
              <div className="text-xl md:text-2xl font-bold text-gray-900">
                {(employees || []).filter(e => e.subordinateCount > 0).length}
              </div>
              <div className="text-xs md:text-sm text-gray-600">Managers</div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow border border-gray-200 p-4 md:p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0 bg-orange-500 rounded-md p-2 md:p-3">
              <Globe className="h-5 w-5 md:h-6 md:w-6 text-white" />
            </div>
            <div className="ml-3 md:ml-4">
              <div className="text-xl md:text-2xl font-bold text-gray-900">
                {(employees || []).reduce((total, e) => total + (e.roleAssignments?.length || 0), 0)}
              </div>
              <div className="text-xs md:text-sm text-gray-600">Rôles attribués</div>
            </div>
          </div>
        </div>
      </div>

      {/* Barre de recherche et filtres */}
      <div className="bg-white rounded-lg shadow border border-gray-200 p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher un employé..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Tous les statuts</option>
              <option value="active">Actifs</option>
              <option value="inactive">Inactifs</option>
            </select>
            <select
              value={qhseFilter}
              onChange={(e) => setQhseFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">Tous les profils</option>
              <option value="qhse">QHSE uniquement</option>
              <option value="non-qhse">Non QHSE</option>
            </select>
            <div className="flex items-center space-x-2">
              {selectedRows.length > 0 && (
                <button
                  onClick={handleBulkDelete}
                  className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                >
                  <Trash2 className="h-4 w-4" />
                  <span>Supprimer ({selectedRows.length})</span>
                </button>
              )}
              {(searchTerm || statusFilter !== 'all' || qhseFilter !== 'all') && (
                <button
                  onClick={() => { setSearchTerm(''); setStatusFilter('all'); setQhseFilter('all'); }}
                  className="flex items-center space-x-1 px-3 py-2 border border-gray-300 rounded-md text-gray-600 hover:bg-gray-50"
                >
                  <X className="h-4 w-4" />
                  <span>Reset</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Table des employés */}
      <div className="bg-white rounded-lg shadow border border-gray-200">
        <DataTable
          data={filteredEmployees}
          columns={columns}
          loading={loading}
          selectable={true}
          onSelectionChange={setSelectedRows}
          onRowDoubleClick={(employee) => setSelectedEmployeeId(employee.id)}
          emptyMessage="Aucun employé ne correspond aux critères"
        />
      </div>

      {/* Formulaires */}
      {showQuickForm && (
        <EmployeeQuickForm
          employee={null}
          onClose={async () => { setShowQuickForm(false); await refresh(); }}
        />
      )}

      {selectedEmployeeId && (
        <EmployeeDetailForm
          employeeId={selectedEmployeeId}
          onClose={handleDetailFormClose}
          onSave={async () => { await refresh(); }}
        />
      )}
    </div>
  );
};

export default EmployeeManagement;
